/* ==========================================================================
   y2k-camera-ui.js  -  vanilla behaviours for the COVA product page. No libraries.
   Everything is driven by data attributes; see docs/BUILD.md for one HTML
   example of each.

     data-cova-countdown        ticking HRS / MINS / SECS boxes
     data-cova-slider           scroll-snap slider (arrows, dots, peek)
     data-cova-tabs             tab buttons + panels
     data-cova-accordion        single-open accordion with height animation
     data-cova-timeline         vertical line that fills while scrolling
     data-cova-sticky-bar       bottom bar shown after an element scrolls away
     data-cova-scroll-to        smooth scroll to a selector

   Public API: window.CovaUI.init(root) re-scans a subtree (called automatically on
   load and on Shopify's `shopify:section:load` editor event).
   ========================================================================== */
(function () {
  'use strict';
  if (window.CovaUI) return;

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  /** matching elements inside root, including root itself */
  function find(root, sel) {
    var list = $$(sel, root);
    if (root.matches && root.matches(sel)) list.unshift(root);
    return list;
  }
  /** mark an element as initialised; false if it already was */
  function once(el, name) {
    var k = 'covaInit' + name;
    if (el.dataset[k]) return false;
    el.dataset[k] = '1';
    return true;
  }
  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }
  function ownedBy(root, attr, el) {
    var owner = el.parentElement && el.parentElement.closest('[' + attr + ']');
    return owner === root;
  }

  /* ---------------------------------------------------------------- scroll loop
     One rAF-throttled scroll/resize listener drives everything that follows scroll. */
  var scrollTasks = [];
  var scrollQueued = false;
  function runScrollTasks() {
    scrollQueued = false;
    for (var i = 0; i < scrollTasks.length; i++) {
      try {
        scrollTasks[i]();
      } catch (e) {}
    }
  }
  function queueScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    window.requestAnimationFrame(runScrollTasks);
  }
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll);
  window.addEventListener('load', queueScroll);

  /* ---------------------------------------------------------------- countdown
     <div data-cova-countdown="120" data-cova-countdown-key="sale">
       <b data-cova-hours>01</b> <b data-cova-mins>59</b> <b data-cova-secs>59</b>
     </div>
     Value = cycle length in minutes (default 120). The per-visitor deadline lives in
     localStorage under `cova:cd:<key>`; at zero a new cycle starts. */
  var countdowns = [];
  var deadlines = {};
  var countdownTimer = null;

  function deadlineFor(key, minutes) {
    var now = Date.now();
    var dur = minutes * 60000;
    var d = deadlines[key] || 0;
    if (!d) {
      try {
        d = parseInt(window.localStorage.getItem('cova:cd:' + key), 10) || 0;
      } catch (e) {}
    }
    // missing, expired, or longer than one cycle (settings changed) -> start fresh
    if (!d || d <= now || d - now > dur) {
      d = now + dur;
      try {
        window.localStorage.setItem('cova:cd:' + key, String(d));
      } catch (e) {}
    }
    deadlines[key] = d;
    return d;
  }

  function tickCountdowns() {
    for (var i = 0; i < countdowns.length; i++) {
      var c = countdowns[i];
      var left = deadlines[c.key] - Date.now();
      if (left <= 0) {
        deadlines[c.key] = 0;
        deadlineFor(c.key, c.minutes);
        left = deadlines[c.key] - Date.now();
      }
      var total = Math.max(0, Math.floor(left / 1000));
      var h = Math.floor(total / 3600);
      var m = Math.floor((total % 3600) / 60);
      var s = total % 60;
      if (c.h) c.h.textContent = pad2(h);
      if (c.m) c.m.textContent = pad2(m);
      if (c.s) c.s.textContent = pad2(s);
    }
  }

  function initCountdown(el) {
    if (!once(el, 'Countdown')) return;
    var minutes = parseFloat(el.getAttribute('data-cova-countdown')) || 120;
    var key = el.getAttribute('data-cova-countdown-key') || 'sale';
    deadlineFor(key, minutes);
    countdowns.push({
      el: el,
      key: key,
      minutes: minutes,
      h: $('[data-cova-hours]', el),
      m: $('[data-cova-mins]', el),
      s: $('[data-cova-secs]', el)
    });
    tickCountdowns();
    if (!countdownTimer) countdownTimer = window.setInterval(tickCountdowns, 250);
  }

  /* ---------------------------------------------------------------- slider
     <div class="cova-slider" data-cova-slider [data-cova-slider-peek] [data-cova-slider-loop]>
       <div class="cova-slider__track" data-cova-slider-track>
         <div class="cova-slider__slide">...</div> ...
       </div>
       <button data-cova-slider-prev>  <button data-cova-slider-next>
       <div data-cova-slider-dots></div>
     </div>
     Fires `cova:slide` on the root: detail { index, count }. root.covaSlider = { goTo, next, prev }. */
  function initSlider(root) {
    if (!once(root, 'Slider')) return;
    var track = $('[data-cova-slider-track]', root) || $('.cova-slider__track', root);
    if (!track) return;
    var prev = $('[data-cova-slider-prev]', root);
    var next = $('[data-cova-slider-next]', root);
    var dotsWrap = $('[data-cova-slider-dots]', root);
    var loop = root.hasAttribute('data-cova-slider-loop');
    var slides = [];
    var dots = [];
    var current = -1;

    function readSlides() {
      slides = Array.prototype.filter.call(track.children, function (c) {
        return c.offsetParent !== null || c.offsetWidth > 0;
      });
    }
    function padLeft() {
      var v = parseFloat(window.getComputedStyle(track).scrollPaddingLeft);
      return isNaN(v) ? 0 : v;
    }
    function targetLeft(i) {
      return Math.max(0, slides[i].offsetLeft - padLeft());
    }
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      dots = slides.map(function (_, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'cova-slider__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', function () {
          goTo(i);
        });
        dotsWrap.appendChild(b);
        return b;
      });
    }
    function setDisabled(btn, off) {
      if (!btn) return;
      btn.disabled = off;
      btn.classList.toggle('is-disabled', off);
      btn.setAttribute('aria-disabled', off ? 'true' : 'false');
    }
    function indexFromScroll() {
      if (!slides.length) return 0;
      var max = track.scrollWidth - track.clientWidth;
      if (max > 2 && track.scrollLeft >= max - 2) return slides.length - 1;
      var x = track.scrollLeft;
      var best = 0;
      var bestD = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(targetLeft(i) - x);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    }
    function update(force) {
      if (!slides.length) return;
      var i = indexFromScroll();
      var max = track.scrollWidth - track.clientWidth;
      setDisabled(prev, !loop && track.scrollLeft <= 2);
      setDisabled(next, !loop && (i >= slides.length - 1 || track.scrollLeft >= max - 2));
      if (i === current && !force) return;
      current = i;
      dots.forEach(function (d, n) {
        d.classList.toggle('is-active', n === i);
        if (n === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      slides.forEach(function (s, n) {
        s.classList.toggle('is-active', n === i);
      });
      root.dispatchEvent(new CustomEvent('cova:slide', { bubbles: true, detail: { index: i, count: slides.length } }));
    }
    function goTo(i) {
      if (!slides.length) return;
      if (i < 0) i = loop ? slides.length - 1 : 0;
      if (i > slides.length - 1) i = loop ? 0 : slides.length - 1;
      track.scrollTo({ left: targetLeft(i), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    function step(dir) {
      goTo(indexFromScroll() + dir);
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = 0;
        update();
      });
    }, { passive: true });

    function refresh() {
      var n = slides.length;
      readSlides();
      if (slides.length !== n || (dotsWrap && !dots.length)) buildDots();
      update(true);
    }
    refresh();
    if (window.ResizeObserver) {
      // also fires when a hidden tab panel becomes visible
      new window.ResizeObserver(function () {
        refresh();
      }).observe(track);
    } else {
      window.addEventListener('resize', refresh);
    }
    root.covaSlider = {
      goTo: goTo,
      next: function () { step(1); },
      prev: function () { step(-1); },
      index: function () { return indexFromScroll(); },
      refresh: refresh
    };
  }

  /* ---------------------------------------------------------------- tabs
     <div data-cova-tabs>
       <button data-cova-tab="a" class="is-active">A</button> <button data-cova-tab="b">B</button>
       <div data-cova-panel="a">...</div> <div data-cova-panel="b">...</div>
     </div>
     Active tab and panel get `is-active`; inactive panels get `hidden`. Fires `cova:tab-shown`
     on the panel (bubbles). Tabs can contain sliders, accordions and timelines. */
  function initTabs(root) {
    if (!once(root, 'Tabs')) return;
    var tabs = $$('[data-cova-tab]', root).filter(function (t) { return ownedBy(root, 'data-cova-tabs', t); });
    var panels = $$('[data-cova-panel]', root).filter(function (p) { return ownedBy(root, 'data-cova-tabs', p); });
    if (!tabs.length) return;
    var uid = 'cova-tabs-' + Math.random().toString(36).slice(2, 7);
    if (tabs[0].parentElement && !tabs[0].parentElement.getAttribute('role')) tabs[0].parentElement.setAttribute('role', 'tablist');

    tabs.forEach(function (t, i) {
      var id = t.getAttribute('data-cova-tab');
      var panel = panels.filter(function (p) { return p.getAttribute('data-cova-panel') === id; })[0];
      t.setAttribute('role', 'tab');
      t.id = t.id || uid + '-t' + i;
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.id = panel.id || uid + '-p' + i;
        panel.setAttribute('aria-labelledby', t.id);
        t.setAttribute('aria-controls', panel.id);
      }
      t.addEventListener('click', function () { select(id, false); });
      t.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'Home' && k !== 'End') return;
        e.preventDefault();
        var n = k === 'Home' ? 0 : k === 'End' ? tabs.length - 1 : (i + (k === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        select(tabs[n].getAttribute('data-cova-tab'), true);
      });
    });

    function select(id, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-cova-tab') === id;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panels.forEach(function (p) {
        var on = p.getAttribute('data-cova-panel') === id;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
        if (on) {
          p.dispatchEvent(new CustomEvent('cova:tab-shown', { bubbles: true }));
        }
      });
      queueScroll();
    }

    var start = tabs.filter(function (t) { return t.classList.contains('is-active') || t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
    select(start.getAttribute('data-cova-tab'), false);
  }

  /* ---------------------------------------------------------------- accordion
     <div data-cova-accordion>            (value "multiple" allows several open)
       <div data-cova-accordion-item [class="is-open"]>
         <button data-cova-accordion-trigger>Question</button>
         <div data-cova-accordion-panel><div class="inner">Answer</div></div>
       </div>
     </div>
     The panel itself must have no padding/border (it animates its height); pad the inner div. */
  function initAccordion(root) {
    if (!once(root, 'Accordion')) return;
    var multiple = root.getAttribute('data-cova-accordion') === 'multiple';
    var items = $$('[data-cova-accordion-item]', root).filter(function (it) { return ownedBy(root, 'data-cova-accordion', it); });

    function parts(item) {
      return { trigger: $('[data-cova-accordion-trigger]', item), panel: $('[data-cova-accordion-panel]', item) };
    }
    function setClosed(p) {
      p.panel.style.height = '0px';
      p.panel.style.visibility = 'hidden';
    }
    function open(item) {
      var p = parts(item);
      if (!p.panel) return;
      item.classList.add('is-open');
      if (p.trigger) p.trigger.setAttribute('aria-expanded', 'true');
      p.panel.style.visibility = 'visible';
      if (reduceMotion) {
        p.panel.style.height = 'auto';
        return;
      }
      p.panel.style.height = p.panel.scrollHeight + 'px';
      var done = function (e) {
        if (e && e.target !== p.panel) return;
        p.panel.removeEventListener('transitionend', done);
        if (item.classList.contains('is-open')) p.panel.style.height = 'auto';
      };
      p.panel.addEventListener('transitionend', done);
    }
    function close(item) {
      var p = parts(item);
      if (!p.panel) return;
      item.classList.remove('is-open');
      if (p.trigger) p.trigger.setAttribute('aria-expanded', 'false');
      if (reduceMotion) {
        setClosed(p);
        return;
      }
      p.panel.style.height = p.panel.scrollHeight + 'px'; // from auto to a number
      void p.panel.offsetHeight; // reflow so the transition starts
      p.panel.style.height = '0px';
      var done = function (e) {
        if (e && e.target !== p.panel) return;
        p.panel.removeEventListener('transitionend', done);
        if (!item.classList.contains('is-open')) p.panel.style.visibility = 'hidden';
      };
      p.panel.addEventListener('transitionend', done);
    }

    items.forEach(function (item) {
      var p = parts(item);
      if (!p.panel) return;
      p.panel.style.overflow = 'hidden';
      p.panel.style.transition = reduceMotion ? 'none' : 'height 0.32s ease';
      if (p.trigger) {
        p.panel.id = p.panel.id || 'cova-acc-' + Math.random().toString(36).slice(2, 8);
        p.trigger.setAttribute('aria-controls', p.panel.id);
      }
      if (item.classList.contains('is-open')) {
        p.panel.style.height = 'auto';
        if (p.trigger) p.trigger.setAttribute('aria-expanded', 'true');
      } else {
        setClosed(p);
        if (p.trigger) p.trigger.setAttribute('aria-expanded', 'false');
      }
      if (p.trigger) {
        p.trigger.addEventListener('click', function () {
          var isOpen = item.classList.contains('is-open');
          if (isOpen) {
            close(item);
          } else {
            if (!multiple) items.forEach(function (o) { if (o !== item && o.classList.contains('is-open')) close(o); });
            open(item);
          }
          window.setTimeout(queueScroll, 340);
        });
      }
    });
  }

  /* ---------------------------------------------------------------- timeline fill
     <div data-cova-timeline [data-cova-timeline-offset="0.6"]>
       <div class="line" data-cova-timeline-line><div data-cova-timeline-fill></div></div>
       <div data-cova-timeline-marker>...</div> ...
     </div>
     The fill grows with scroll (reference line = 60% down the viewport). Sets
     --cova-timeline-fill (px) and --cova-timeline-progress (0-1) on the root, sets the fill
     element's height, and adds `is-done` to every marker the fill has passed. */
  function initTimeline(root) {
    if (!once(root, 'Timeline')) return;
    var line = $('[data-cova-timeline-line]', root) || root;
    var fill = $('[data-cova-timeline-fill]', root);
    var markers = $$('[data-cova-timeline-marker]', root);
    var offset = parseFloat(root.getAttribute('data-cova-timeline-offset'));
    if (isNaN(offset)) offset = 0.6;
    function update() {
      var r = line.getBoundingClientRect();
      if (!r.height) return; // hidden (inactive tab)
      var ref = window.innerHeight * offset;
      var filled = Math.max(0, Math.min(r.height, ref - r.top));
      root.style.setProperty('--cova-timeline-fill', filled + 'px');
      root.style.setProperty('--cova-timeline-progress', (filled / r.height).toFixed(4));
      if (fill) fill.style.height = filled + 'px';
      for (var i = 0; i < markers.length; i++) {
        var mr = markers[i].getBoundingClientRect();
        markers[i].classList.toggle('is-done', mr.top + mr.height / 2 - r.top <= filled);
      }
    }
    scrollTasks.push(update);
    root.addEventListener('cova:tab-shown', queueScroll);
    update();
  }

  /* ---------------------------------------------------------------- sticky bar
     <div data-cova-sticky-bar data-cova-sticky-after="#cova-offers" [data-cova-sticky-hide-near="#footer"]>
     Gets `is-visible` once the `after` element's bottom has scrolled above the viewport top
     (if the element is missing: after one viewport of scrolling). With hide-near, it hides
     again while that element's top is inside the viewport. Sets --cova-sticky-h on <html>. */
  function initStickyBar(bar) {
    if (!once(bar, 'Sticky')) return;
    var afterSel = bar.getAttribute('data-cova-sticky-after');
    var nearSel = bar.getAttribute('data-cova-sticky-hide-near');
    function update() {
      var after = afterSel ? $(afterSel) : null;
      var show;
      if (after) show = after.getBoundingClientRect().bottom < 0;
      else show = window.scrollY > window.innerHeight;
      if (show && nearSel) {
        var near = $(nearSel);
        if (near && near.getBoundingClientRect().top < window.innerHeight - 8) show = false;
      }
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
    }
    scrollTasks.push(update);
    document.documentElement.style.setProperty('--cova-sticky-h', bar.offsetHeight + 'px');
    update();
  }

  /* ---------------------------------------------------------------- scroll-to
     <button data-cova-scroll-to="#cova-offers">  - delegated, works for later-added elements */
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-cova-scroll-to]') : null;
    if (!t) return;
    var dest = $(t.getAttribute('data-cova-scroll-to'));
    if (!dest) return;
    e.preventDefault();
    dest.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------------------------------------------------------------- init */
  function init(root) {
    root = root || document;
    find(root, '[data-cova-countdown]').forEach(initCountdown);
    find(root, '[data-cova-tabs]').forEach(initTabs);
    find(root, '[data-cova-slider]').forEach(initSlider);
    find(root, '[data-cova-accordion]').forEach(initAccordion);
    find(root, '[data-cova-timeline]').forEach(initTimeline);
    find(root, '[data-cova-sticky-bar]').forEach(initStickyBar);
    queueScroll();
  }

  window.CovaUI = { init: init, version: '1.0.0' };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
